import React, { useEffect } from 'react';

import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import CustomField from '@/components/CustomFields/CustomField';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { supabase } from '@/data/client';
import { saveSmallGroup } from '@/data/smallGroups';
import Toast from 'react-native-toast-message';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { showAlert } from '@/utils/showAlert';
import Callout from '@/components/General/Callout';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import BibleStudySetupNeeded from '@/components/BibleStudies/BibleStudySetupNeeded';

const EMPTY_DRAFT = {};

export default function AddBibleStudyModal() {
    const { id, fromScreen, fromStoreKey } = useLocalSearchParams();
    const storeKey = id || "new"

    const queryClient = useQueryClient()
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const router = useRouter();

    const studyDetails = useEditScreenStore(s => s.AddBibleStudyModal?.[storeKey]) || EMPTY_DRAFT

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: existingItem } = Query.smallGroups.byId({id: id, options: { enabled: !!id }});

    const { enabled, loading, canView, canManage, setupNeeded, bibleStudyType, roleID } = useBibleStudies();

    // Flag/permission guard — UI gating only; backend enforces regardless.
    useEffect(() => {
        if (!loading && (!enabled || !canView)) {
            router.canGoBack() ? router.back() : router.replace("/");
        }
    }, [loading, enabled, canView, router]);

    const { data: bibleStudyTypeSections } = Query.customFieldSections.all({module: "SmallGroupType", options: { enabled: !!bibleStudyType?.id }});

    // Teacher roster = RolePeople of the type's roleID — pickers are limited to
    // rostered teachers (backend validates assignments at write time too).
    const rolePeopleQuery = useInfiniteQuery({
        queryKey: ['peopleByRole', roleID],
        queryFn: async ({pageParam: _pageParam = 0}) => {
            const { data, error } = await supabase.from('RolePeople')
                .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('roleID', roleID);
            if (error) throw error;
            const people = data?.map(r => r.Person).filter(Boolean) || [];
            people.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
            return { nextPage: undefined, data: people };
        },
        getNextPageParam: () => undefined,
        enabled: !!roleID
    });

    const saveStudyMutation = useMutation({
        mutationFn: saveSmallGroup,
        onSuccess: (data) => {
            removeEditScreenData("AddBibleStudyModal", storeKey)
            queryClient.invalidateQueries({queryKey: ['bibleStudies']});
            queryClient.invalidateQueries({queryKey: ['smallGroups']});
            queryClient.invalidateQueries({queryKey: ['dashboardData']});
            queryClient.invalidateQueries({queryKey: ['people']});
            Toast.delayShow({type: 'success', text2: `Bible Study has been ${id ? 'updated' : 'created'} successfully!`})

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                if(id){
                    redirectToDefault(router, "AddBibleStudyModal")
                }
                else{
                    router.replace({pathname: "/biblestudies/sessions/create", params: {smallGroupID: data.id}})
                }
            }
        }
    })

    /*** Effects ***/
    useEffect(() => {
        // Self-service creation assigns the caller as primary teacher. Wait for
        // loggedInUser to resolve on cold mounts before defaulting.
        if(!existingItem && loggedInUser && !studyDetails?.Leader){
            setStudyDetails({ ...studyDetails, leaderID: loggedInUser.id, Leader: loggedInUser, Students: studyDetails?.Students || [], smallGroupTypeID: bibleStudyType?.id, SmallGroupType: bibleStudyType })
        }
    }, [loggedInUser, bibleStudyType, existingItem])

    useEffect(() => {
        if(!existingItem && bibleStudyType?.id && !studyDetails?.smallGroupTypeID){
            setStudyDetails({...studyDetails, smallGroupTypeID: bibleStudyType.id, SmallGroupType: bibleStudyType})
        }
    }, [bibleStudyType, existingItem])

    useEffect(() => {
        if(existingItem){
            setStudyDetails({
                ...existingItem,
                Students: existingItem?.Students?.map(s => s.Person),
                originalValues: existingItem,
            })
        }
    }, [existingItem]);

    const setStudyDetails = (details) => {
        setEditScreenData("AddBibleStudyModal", storeKey, details)
    }

    if (enabled && setupNeeded && !loading) {
        return <BibleStudySetupNeeded canManage={canManage} />;
    }

    return (
        <ModalWrapper
            loading={saveStudyMutation.isPending}
            onConfirm={() => {
                const studyToSubmit = {...studyDetails}

                if(!studyToSubmit.Leader){
                    Toast.show({type: 'error', text2: 'Please select a Primary Teacher'})
                    return;
                }

                if(!studyToSubmit.Students?.length){
                    Toast.show({type: 'error', text2: 'Please select at least one Student'})
                    return;
                }

                const missingRequiredFields = (bibleStudyType?.CustomFields || []).filter(f => f.displayOn !== 'session' && f.required && !f.isDeleted);
                const missingField = missingRequiredFields.find(f => {
                    const val = studyToSubmit.customFieldValues?.[f.id];
                    return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                });
                if(missingField){
                    Toast.show({type: 'error', text2: `Please fill out the required field: ${missingField.name}`})
                    return;
                }

                // Non-managers can only self-assign as primary teacher (the
                // backend enforces this too — this is the UI hint).
                if(!canManage && !id && studyToSubmit.leaderID !== loggedInUser?.id){
                    Toast.show({type: 'error', text2: 'Only Bible Study managers can assign a different Primary Teacher'})
                    return;
                }

                if(!studyToSubmit.name){
                    studyToSubmit.name = studyToSubmit.Students?.map(s => s.firstName).join(', ')
                }

                const { originalValues, ...cleanSubmit } = studyToSubmit;
                const submit = () => saveStudyMutation.mutate({...cleanSubmit, originalValues});

                const actorIsTeacher = loggedInUser?.id === studyToSubmit.leaderID || loggedInUser?.id === studyToSubmit.coLeaderID;
                const originalMemberIDs = (originalValues?.Students || []).map(s => s.Person?.id);
                const addedStudents = id ? (studyToSubmit.Students || []).filter(s => !originalMemberIDs.includes(s.id)) : (studyToSubmit.Students || []);

                if(studyToSubmit.notifyLeaderOnMemberAdd && !actorIsTeacher && addedStudents.length > 0){
                    const personName = p => `${p?.firstName || ''} ${p?.lastName || ''}`.trim();
                    const teacherName = personName(studyToSubmit.Leader);
                    const coTeacherName = personName(studyToSubmit.CoLeader);
                    const studentNames = addedStudents.map(personName).join(', ');
                    showAlert({
                        title: 'Notify Teacher?',
                        subtitle: `${teacherName}${coTeacherName ? ' and ' + coTeacherName : ''} will be notified that ${studentNames} will be added to their study`,
                        confirmText: 'Save',
                        cancelText: 'Cancel',
                        onConfirm: submit,
                    });
                    return;
                }

                submit();
            }}
            title={id ? `Update Bible Study` : `New Bible Study`}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                <InputSection title="Basic Info">
                    {!canManage && !id ? (
                        <>
                            <SheetSelector
                                required
                                type="people"
                                disabled
                                title="Primary Teacher"
                                helpText="You are the primary teacher of this study. Only Bible Study managers can assign someone else."
                                value={loggedInUser}
                                onChange={() => {}}
                            />
                            <Callout type="info" message="You're creating this study as its primary teacher. Bible Study managers can assign a different primary." />
                        </>
                    ) : (
                        <SheetSelector
                            required
                            type="people"
                            queryResult={roleID ? rolePeopleQuery : undefined}
                            title="Primary Teacher"
                            helpText="Select the rostered teacher who will lead this study."
                            fromScreen={`biblestudies/create`}
                            fromStoreKey={storeKey}
                            value={studyDetails?.Leader}
                            onChange={(selected_item) => {
                                setStudyDetails({
                                    leaderID: selected_item?.id,
                                    Leader: selected_item
                                })
                            }}
                        />
                    )}

                    <SheetSelector
                        type="people"
                        queryResult={roleID ? rolePeopleQuery : undefined}
                        title="Co-Teacher"
                        helpText="Optionally select a co-teacher who shares responsibility for this study."
                        fromScreen={`biblestudies/create`}
                        fromStoreKey={storeKey}
                        value={studyDetails?.CoLeader}
                        onChange={(selected_item) => {
                            setStudyDetails({
                                coLeaderID: selected_item?.id,
                                CoLeader: selected_item
                            })
                        }}
                    />

                    <SheetSelector required multiple type="people" title="Students" helpText="Select the students who will regularly attend this study. You will be able to alter this list for each session." addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null} fromScreen={`biblestudies/create`} fromStoreKey={storeKey} value={studyDetails.Students || []} onChange={(selected_items) => {
                        setStudyDetails({
                            Students: selected_items
                        })
                    }}/>

                    <Input
                        title="Study Name (optional)"
                        maxLength={120}
                        value={studyDetails.name}
                        onUpdate={(new_value) => {
                            setStudyDetails({
                                name: new_value
                            })
                        }}
                    />

                    <Input
                        title="Description (optional)"
                        helpText="A short blurb describing this study."
                        multiline
                        maxLength={2000}
                        value={studyDetails.description}
                        onUpdate={(new_value) => {
                            setStudyDetails({
                                description: new_value
                            })
                        }}
                    />

                    <ToggleRow
                        testID="notify-teacher-toggle"
                        label="Notify the teacher when students are added"
                        description="Sends a push notification and email to the primary teacher and co-teacher when someone else adds students to this study."
                        value={!!studyDetails.notifyLeaderOnMemberAdd}
                        onValueChange={(v) => setStudyDetails({ notifyLeaderOnMemberAdd: v })}
                    />
                </InputSection>

                {(bibleStudyTypeSections || []).map(section => {
                    const sectionFields = (bibleStudyType?.CustomFields || []).filter(f => f.sectionID === section.id && f.displayOn !== 'session');
                    if (sectionFields.length === 0) return null;
                    return (
                        <InputSection key={section.id} title={section.name}>
                            {sectionFields.map(field => (
                                <CustomField
                                    key={field.id}
                                    fieldType={field.type}
                                    required={field.required}
                                    onUpdate={(val) => {
                                        const oldVals = studyDetails.customFieldValues || {};
                                        setStudyDetails({ customFieldValues: { ...oldVals, [field.id]: val } });
                                    }}
                                    options={{
                                        title: field.name,
                                        value: studyDetails.customFieldValues?.[field.id],
                                        options: field.options,
                                    }}
                                />
                            ))}
                        </InputSection>
                    );
                })}

            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}
